import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function WizardLayout({
  children,
  currentStep,
  totalSteps,
  onBack,
  onContinue,
  canContinue = true,
  isLoading = false,
  showBack = true,
  continueLabel = 'Continue',
  previewPanel = null,
  contentClassName = 'max-w-xl',
}) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      {/* Step Indicator */}
      <div className="fixed top-0 left-0 right-0 z-50 pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)] bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl tracking-wide text-white" style={{ fontFamily: 'var(--s8-font-mono)', fontWeight: 500 }}>
              STAGE8
            </span>
            <span className="text-white/40 text-xs">
              {currentStep} of {totalSteps}
            </span>
          </div>
          {/* Progress bar — jumps per completed step, starts with small sliver */}
          <div className="w-full h-[2px] bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--s8-amber)] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.max(3, ((currentStep - 1) / totalSteps) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex pt-16 pb-28">
        <div className="flex-1 flex flex-col items-center px-6 py-4">
          <div className={cn('w-full', contentClassName)}>
            {children}
          </div>
        </div>

        {/* Desktop Preview Panel */}
        {previewPanel && (
          <div className="hidden lg:block w-80 border-l border-white/[0.06] p-6 bg-[#0d0d0d]">
            <div className="sticky top-24">
              <h3 className="text-[10px] text-white/40 uppercase tracking-wider font-medium mb-4">
                Card Preview
              </h3>
              {previewPanel}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)] pb-[env(safe-area-inset-bottom,0px)] bg-[#0a0a0a]/95 backdrop-blur-sm border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          {showBack && currentStep > 1 ? (
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="wizard-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          ) : (
            <div />
          )}
          
          <Button
            onClick={onContinue}
            disabled={!canContinue || isLoading}
            className={cn(
              'min-w-[140px] h-12 font-semibold uppercase tracking-wide transition-all',
              canContinue
                ? 'bg-amber-500 hover:bg-amber-400 text-black'
                : 'bg-white/10 text-white/30'
            )}
            data-testid="wizard-continue-btn"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <div className="h-4 w-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <>
                {continueLabel}
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WizardQuestion({ title, subtitle }) {
  return (
    <div className="mb-8 text-center">
      <h1 className="text-2xl md:text-3xl font-semibold text-white leading-tight mb-2">
        {title}
      </h1>
      {subtitle && (
        <p className="text-white/50 text-sm md:text-base">
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function WizardSection({ children, className }) {
  return (
    <div className={cn('surface-card p-6 rounded-xl', className)}>
      {children}
    </div>
  );
}
